import { supabase } from '@/lib/supabase';
import type { Group, GroupMember, User, CreateGroupData, UpdateGroupData } from '@sharecost/shared';

export interface GroupWithMembers extends Group {
  members: (GroupMember & { user: User })[];
}

// Group IDs the user belongs to, partitioned by their per-user `archived` flag.
// `!== true` keeps pre-migration rows with NULL on the active side (matches the
// mobile `archived: { $ne: true }` idiom).
async function fetchMembershipGroupIds(userId: string, archived: boolean): Promise<string[]> {
  const { data, error } = await supabase
    .from('group_members')
    .select('group_id, archived')
    .eq('user_id', userId)
    .eq('_deleted', false);

  if (error) throw error;
  return (data ?? [])
    .filter(m => (archived ? m.archived === true : m.archived !== true))
    .map(m => m.group_id);
}

async function fetchGroupsByIds(groupIds: string[]): Promise<GroupWithMembers[]> {
  if (!groupIds.length) return [];

  const { data: groups, error } = await supabase
    .from('groups')
    .select(`
      *,
      group_members(
        id, group_id, user_id, role, joined_at, display_order, archived,
        users(id, email, display_name, avatar_url, default_currency, is_virtual, created_by_user_id, subscription_tier)
      )
    `)
    .in('id', groupIds)
    .eq('_deleted', false)
    .eq('group_members._deleted', false)
    .order('created_at', { ascending: false });

  if (error) throw error;

  return (groups ?? []).map(g => ({
    ...g,
    members: (g.group_members ?? []).map((m: any) => ({
      ...m,
      user: m.users,
    })),
  })) as GroupWithMembers[];
}

/** Active (non-archived) groups for the user. */
export async function fetchUserGroups(userId: string): Promise<GroupWithMembers[]> {
  return fetchGroupsByIds(await fetchMembershipGroupIds(userId, false));
}

/** Groups the user has archived (for the "Archived" section / unarchive UI). */
export async function fetchArchivedGroups(userId: string): Promise<GroupWithMembers[]> {
  return fetchGroupsByIds(await fetchMembershipGroupIds(userId, true));
}

/** Set the current user's per-group archive flag (does not touch group data). */
export async function setGroupArchived(
  groupId: string,
  userId: string,
  archived: boolean
): Promise<void> {
  const { error } = await supabase
    .from('group_members')
    .update({ archived })
    .eq('group_id', groupId)
    .eq('user_id', userId);

  if (error) throw error;
}

export async function fetchGroupDetail(groupId: string): Promise<GroupWithMembers | null> {
  const { data, error } = await supabase
    .from('groups')
    .select(`
      *,
      group_members(
        id, group_id, user_id, role, joined_at, display_order, archived,
        users(id, email, display_name, avatar_url, default_currency, is_virtual, created_by_user_id, subscription_tier, payment_methods, payment_qr_enabled)
      )
    `)
    .eq('id', groupId)
    .eq('_deleted', false)
    .eq('group_members._deleted', false)
    // maybeSingle: a group deleted or left on another device must refetch to
    // null so the detail page can leave; single() throws and keeps stale data.
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  return {
    ...data,
    members: (data.group_members ?? []).map((m: any) => ({
      ...m,
      user: m.users,
    })),
  } as GroupWithMembers;
}

export async function createGroup(data: CreateGroupData, userId: string): Promise<Group> {
  const id = crypto.randomUUID();
  const now = new Date().toISOString();

  const { data: group, error: groupError } = await supabase
    .from('groups')
    .insert({
      id,
      name: data.name,
      description: data.description ?? null,
      currency_code: data.currency_code,
      created_by: userId,
      color: data.color ?? null,
      created_at: now,
    })
    .select()
    .single();

  if (groupError) throw groupError;

  // Add creator as admin member
  const { error: memberError } = await supabase
    .from('group_members')
    .insert({
      id: crypto.randomUUID(),
      group_id: id,
      user_id: userId,
      role: 'admin',
      joined_at: now,
    });

  if (memberError) throw memberError;

  // Mirror the mobile app: every group gets a permanent invite code at creation,
  // otherwise the mobile "Invite" flow had nothing to share. A failure here must
  // not fail group creation; the admin can still generate a code from the group page.
  let inviteCode: string | null = null;
  try {
    inviteCode = await regenerateInviteCode(id);
  } catch (err) {
    console.warn('[groups] Could not generate invite code for new group', err);
  }

  return { ...(group as Group), invite_code: inviteCode ?? undefined };
}

export async function updateGroup(groupId: string, data: UpdateGroupData): Promise<void> {
  const updates: Record<string, unknown> = {};
  if (data.name !== undefined) updates.name = data.name;
  if (data.description !== undefined) updates.description = data.description;
  if (data.baseCurrency !== undefined) updates.currency_code = data.baseCurrency;
  if (data.color !== undefined) updates.color = data.color;
  if (data.budgetEnabled !== undefined) updates.budget_enabled = data.budgetEnabled;
  if (data.budgetAmountMinor !== undefined) updates.budget_amount_minor = data.budgetAmountMinor;
  if (data.budgetPeriod !== undefined) updates.budget_period = data.budgetPeriod;
  if (data.budgetAlertThreshold !== undefined) updates.budget_alert_threshold = data.budgetAlertThreshold;
  if (data.budgetStartDay !== undefined) updates.budget_start_day = data.budgetStartDay;
  if (data.adminsCanEditExpenses !== undefined) updates.admins_can_edit_expenses = data.adminsCanEditExpenses;

  const { error } = await supabase
    .from('groups')
    .update(updates)
    .eq('id', groupId);

  if (error) throw error;
}

export async function joinGroupByInviteCode(inviteCode: string): Promise<{
  group_id: string;
  group_name: string;
}> {
  const { data, error } = await supabase
    .rpc('join_group_by_invite_code', { p_invite_code: inviteCode.trim() });

  if (error) throw error;
  if (!data?.success) throw new Error(data?.message ?? 'Failed to join group');

  return {
    group_id: data.group_id,
    group_name: data.group_name,
  };
}

/**
 * Generate a new invite code for the group (admins only, enforced by the RPC).
 * Codes are permanent by default, matching the mobile app; the mobile client
 * shows no expiry, so a web-side 7-day default produced dead links there.
 */
export async function regenerateInviteCode(
  groupId: string,
  expiresInDays: number | null = null,
  maxUses: number | null = null
): Promise<string> {
  const { data, error } = await supabase.rpc('generate_secure_invite_code', {
    p_group_id: groupId,
    p_expires_in_days: expiresInDays,
    p_max_uses: maxUses,
  });

  if (error) throw error;
  return data as string;
}

export async function leaveGroup(groupId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('group_members')
    .update({ _deleted: true })
    .eq('group_id', groupId)
    .eq('user_id', userId);

  if (error) throw error;
}

export async function deleteGroup(groupId: string): Promise<void> {
  const { error } = await supabase
    .from('groups')
    .update({ _deleted: true })
    .eq('id', groupId);

  if (error) throw error;
}

export interface MemberLookupEntry {
  displayName: string;
  avatarUrl: string | null;
  isFormer: boolean;
}

/**
 * Fetch ALL group members (including soft-deleted) with user details.
 * RLS policy `gm_select` only checks if current user is active member of
 * the group — it does not filter by `_deleted` on the row being read.
 */
export async function fetchAllGroupMemberUsers(
  groupId: string
): Promise<{ resolved: Map<string, MemberLookupEntry>; unresolvedIds: string[] }> {
  const { data, error } = await supabase
    .from('group_members')
    .select('user_id, _deleted, users(display_name, avatar_url)')
    .eq('group_id', groupId);

  if (error) throw error;

  const resolved = new Map<string, MemberLookupEntry>();
  const unresolvedIds: string[] = [];

  for (const row of data ?? []) {
    const user = (row as any).users;
    if (user?.display_name) {
      resolved.set(row.user_id, {
        displayName: user.display_name,
        avatarUrl: user.avatar_url ?? null,
        isFormer: row._deleted === true,
      });
    } else {
      unresolvedIds.push(row.user_id);
    }
  }
  return { resolved, unresolvedIds };
}

/**
 * Fallback: fetch display names from expense_splits snapshots
 * for user IDs that couldn't be resolved via group_members→users join
 * (e.g., user left ALL shared groups, making their users row RLS-invisible).
 */
export async function reorderGroups(
  userId: string,
  groupOrders: Array<{ groupId: string; displayOrder: number }>
): Promise<void> {
  const results = await Promise.all(
    groupOrders.map(({ groupId, displayOrder }) =>
      supabase
        .from('group_members')
        .update({ display_order: displayOrder })
        .eq('group_id', groupId)
        .eq('user_id', userId)
    )
  );

  const error = results.find(r => r.error)?.error;
  if (error) throw error;
}

export async function fetchSnapshotNames(
  groupId: string,
  userIds: string[]
): Promise<Map<string, string>> {
  if (userIds.length === 0) return new Map();

  const { data, error } = await supabase
    .from('expense_splits')
    .select('user_id, participant_display_name')
    .eq('group_id', groupId)
    .in('user_id', userIds)
    .not('participant_display_name', 'is', null)
    .limit(100);

  if (error) throw error;

  const map = new Map<string, string>();
  for (const row of data ?? []) {
    if (row.participant_display_name && !map.has(row.user_id)) {
      map.set(row.user_id, row.participant_display_name);
    }
  }
  return map;
}
